import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import { cn } from "@/lib/utils";

/**
 * OptimizedInput - A debounced input component to prevent lag
 * 
 * This component maintains its own internal state and only propagates
 * changes to the parent after a debounce delay, preventing excessive re-renders.
 */
const OptimizedInput = memo(function OptimizedInput({
  value: externalValue,
  onChange,
  debounceMs = 150,
  className,
  type = "text",
  ...props
}) {
  const [internalValue, setInternalValue] = useState(externalValue || '');
  const timeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Sync internal value when external value changes from outside
  useEffect(() => {
    // Only sync if we're not actively typing (no pending timeout)
    if (!isTypingRef.current) {
      setInternalValue(externalValue || '');
    }
  }, [externalValue]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    isTypingRef.current = true;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the onChange callback
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      isTypingRef.current = false;
      if (onChange) {
        onChange({
          target: { value: newValue, name: props.name },
          currentTarget: { value: newValue, name: props.name }
        });
      }
    }, debounceMs);
  }, [onChange, debounceMs, props.name]);

  // Handle blur - flush pending changes immediately
  const handleBlur = useCallback((e) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      isTypingRef.current = false;
      if (onChange) {
        onChange({
          target: { value: internalValue, name: props.name },
          currentTarget: { value: internalValue, name: props.name }
        });
      }
    }
    if (props.onBlur) {
      props.onBlur(e);
    }
  }, [onChange, internalValue, props.name, props.onBlur]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <input
      type={type}
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(
        "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
});

/**
 * OptimizedTextarea - A debounced textarea component
 */
const OptimizedTextarea = memo(function OptimizedTextarea({
  value: externalValue,
  onChange,
  debounceMs = 150,
  className,
  ...props
}) {
  const [internalValue, setInternalValue] = useState(externalValue || '');
  const timeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (!isTypingRef.current) {
      setInternalValue(externalValue || '');
    }
  }, [externalValue]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    isTypingRef.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      isTypingRef.current = false;
      if (onChange) {
        onChange({
          target: { value: newValue, name: props.name },
          currentTarget: { value: newValue, name: props.name }
        });
      }
    }, debounceMs);
  }, [onChange, debounceMs, props.name]);

  const handleBlur = useCallback((e) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      isTypingRef.current = false;
      if (onChange) {
        onChange({
          target: { value: internalValue, name: props.name },
          currentTarget: { value: internalValue, name: props.name }
        });
      }
    }
    if (props.onBlur) {
      props.onBlur(e);
    }
  }, [onChange, internalValue, props.name, props.onBlur]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <textarea
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
});

export { OptimizedInput, OptimizedTextarea };
